// src/styles/theme.ts
// Savroc ERP – jaettu CSS + vakiot

export const TOKEN_KEY = "savroc_access_token";

/**
 * Siivoa vanhojen versioiden mahdolliset jäännökset selaimen localStoragesta.
 * Nykyinen autentikointi käyttää in-memory access-tokenia + HttpOnly refresh-cookie:tä,
 * EI localStoragea. Tämä helper kutsutaan logout-funktiossa varmistamaan että
 * vanhat asennukset (joissa token saattoi olla localStoragessa) ovat puhtaita.
 * Tämä voidaan poistaa muutaman release-syklin jälkeen kun varmaa että kaikki
 * käyttäjät ovat avanneet sovelluksen uudemmalla versiolla.
 */
export function clearLegacyAuthStorage(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("savroc_user");
    localStorage.removeItem("access_token");
  } catch { /* ignore — esim. private mode tai quota */ }
}

export const BASE: string =
  (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_API_BASE_URL) ??
  "http://localhost:3001/api";


// ── Shared apiFetch ──────────────────────────────────────────────
// Löydös F3: tämä on 125 tiedoston käyttämä apiFetch. Aiemmin siltä puuttui
// 401-refresh + auth:logout KOKONAAN → PWA:n palatessa taustalta (access-token
// vanhentunut) sivut näyttivät "HTTP 401" ilman toipumista. Nyt sama refresh+retry+
// logout-logiikka kuin client.ts:ssä, jaetun single-flight tryRefreshin kautta.
import { getAccessToken, tryRefresh, setAccessToken, RefreshNetworkError } from "../api/client";

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // P2 (28.7.2026): GET-pyynnöille 45 s oletusaikakatkaisu — jumittunut pyyntö
  // jätti näkymän ikuiseen lataustilaan. Mutaatioihin (POST voi olla AI-kutsu,
  // kestää minuutteja) ei kosketa; kutsuja voi ohittaa omalla signalilla.
  const method = (init?.method ?? "GET").toUpperCase();
  const useTimeout = method === "GET" && !init?.signal;

  const doFetch = async () => {
    const token = getAccessToken();
    const isJson = init?.body && typeof init.body === "string";
    const aborter = useTimeout ? new AbortController() : null;
    const timer = aborter ? window.setTimeout(() => aborter.abort(), 45_000) : null;
    try {
      return await fetch(`${BASE}${path}`, {
        credentials: "include",
        ...init,
        headers: {
          ...(isJson ? { "Content-Type": "application/json" } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...((init?.headers as Record<string, string>) ?? {}),
        },
        ...(aborter ? { signal: aborter.signal } : {}),
      });
    } catch (err) {
      if (aborter?.signal.aborted) {
        throw new Error("Pyyntö aikakatkaistiin — tarkista verkkoyhteys ja yritä uudelleen.");
      }
      throw err;
    } finally {
      if (timer != null) window.clearTimeout(timer);
    }
  };

  let res = await doFetch();

  // 401 → yritä token-refresh kerran (jaettu single-flight), sitten uusinta.
  if (res.status === 401) {
    let refreshed = false;
    try {
      refreshed = await tryRefresh();
    } catch (e) {
      if (e instanceof RefreshNetworkError) throw new Error("Yhteysvirhe — tarkista verkkoyhteys ja yritä uudelleen.");
      throw e;
    }
    if (refreshed) {
      res = await doFetch();
    } else {
      setAccessToken(null);
      window.dispatchEvent(new Event("auth:logout"));
      throw new Error("Istunto vanhentunut. Kirjaudu uudelleen.");
    }
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const b = await res.json();
      msg = Array.isArray(b.message) ? b.message.join(", ") : (b.message ?? b.error ?? msg);
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ── Date formatter ───────────────────────────────────────────────
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const s = iso.slice(0, 10);
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
}

// ── File size formatter ──────────────────────────────────────────
export function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ── MIME type → short label ──────────────────────────────────────
export function mimeLabel(mime: string): string {
  const map: Record<string, string> = {
    "application/pdf": "PDF",
    "image/jpeg": "JPEG",
    "image/png": "PNG",
    "image/webp": "WEBP",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  };
  return map[mime] ?? mime.split("/")[1]?.toUpperCase() ?? "FILE";
}
